import {Dialog, Pane, Paragraph} from "evergreen-ui";
import React, {memo, useCallback, useEffect, useState} from "react";
import {useHistory} from "react-router";
import {BehaviorSubject, Subject, zip} from "rxjs";
import {Referral, SelectedReferral} from "../../../referral";
import {useObservable} from "../../../rxsupport";
import {Offer} from "../../domain/types";
import { useApplicationRootUrl } from "common/component/ApplicationRootContext";

export function useSiblingOfferSuggestionDialog(referral: Referral, successCallback: (offer: Partial<Offer>) => any) {
    const [offerSubmissionResult$] = useState(() => new Subject<Partial<Offer>>());
    const [confirmSiblingOfferSuggestion$] = useState(() => new Subject<boolean>());
    const [showSiblingOfferSuggestion$] = useState(() => new BehaviorSubject(false));
    const [resolveUserChoice$] = useState(() => zip([offerSubmissionResult$, confirmSiblingOfferSuggestion$]));

    const history = useHistory();
    const rootUrl = useApplicationRootUrl();

    useEffect(() => {
        const sub = resolveUserChoice$.subscribe(([offer, yes]) => {
            if (yes) {
                const sibling = SelectedReferral.filterSiblingReferralsForOffer(referral)[0];
                history.push(SelectedReferral.createReferralNewOfferLink(sibling.siblingReferralGuid, rootUrl));
            } else {
                successCallback(offer);
            }
        });
        return () => sub.unsubscribe();
    }, [successCallback, referral, history, resolveUserChoice$, rootUrl]);

    return {
        offerSubmissionResult$,
        showSiblingOfferSuggestion$,
        confirmSiblingOfferSuggestion$
    };
}

type Prop = {
    show$: BehaviorSubject<boolean>;
    confirm$: Subject<boolean>;
}

const WIDTH = 600;

export const SiblingOfferSuggestionDialog = memo(function SiblingOfferSuggestionDialog({show$, confirm$}: Prop) {
    const show = useObservable(show$, false);

    const onClose = useCallback(() => {
        confirm$.next(false);
        show$.next(false);
    }, [show$, confirm$]);

    const onYes = useCallback(() => {
        confirm$.next(true);
        show$.next(false);
    }, [show$, confirm$]);

    return (
        <Dialog
            title="Sibling Referral"
            isShown={show}
            width={WIDTH}
            hasClose={false}
            onCloseComplete={onClose}
            contentContainerProps={{
                textAlign: 'center',
                justifyContent: 'center'
            }}
            footer={({close}) => (<>
                {/* minus the default evergreen-ui padding in footer container */}
                <Pane width={WIDTH - 16 * 2} className="flex justify-center">
                    <button
                        autoFocus
                        className="px-5 py-2 mr-5 focus:outline-none"
                        onClick={close}
                        style={{
                            backgroundColor: '#FFF',
                            color: '#00AB93',
                            borderColor: '#00AB93',
                            border: '1px solid'
                        }}
                    >
                        No
                    </button>
                    <button
                        className="px-5 py-2"
                        onClick={onYes}
                        style={{
                            backgroundColor: '#00AB93',
                            color: '#FFF'
                        }}
                    >
                        Yes
                    </button>
                </Pane>
            </>)}
        >
            <Paragraph style={{color: '#707070'}} size={500}>
                This referral has a related sibling referral.
                Would you like to submit an offer for the sibling referral?
            </Paragraph>
        </Dialog>
    );
});
